import { TrackingService } from './core/services/tracking.service';
import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, effect, inject, isDevMode, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { LanguageSelectorComponent } from './shared/components/language-selector/language-selector.component';
import {
  ImpressumModalComponent,
} from './shared/components/impressum-modal/impressum-modal.component';
import { DatenschutzModalComponent } from './shared/components/datenschutz-modal/datenschutz-modal.component';
import { KnowledgeData, KnowledgeService } from './core/services/knowledge.service';
import { ChatService } from './core/services/chat.service';
import { I18nService } from './core/services/i18n.service';
import { TranslatePipe } from './shared/pipes/translate.pipe';

const SESSION_KEY = 'cr_session_id';
const CHAT_MESSAGES_KEY = 'cr_chat_messages';
const SESSION_TTL_MS = 3600 * 1000;
import logoSources from '../../../cr-schupo-backend/templates/logos/sources.json';

const DEFAULT_LOGO_SRC = logoSources.default;
const STANDORT_LOGO_SOURCES: Record<string, string> = logoSources.locations;
const ROOT_KNOWN_KEYS = ['source', 'akte', 'raw_case_json', 'standort', 'rechnungen', 'zahlungen', 'titel', 'raten', 'permissions', 'knowledge', 'mockData'];
const AKTE_MASTER_KEYS = [
  'id',
  'aktennummer',
  'unterakte',
  'standort_clz',
  'privatperson',
  'stadium',
  'gl_name',
  'gl_strasse',
  'gl_plz',
  'gl_ort',
  'imported_at',
  'updated_at',
];
const FORDERUNG_KEYS = [
  'hauptforderung_forderung',
  'zinsen_hf_forderung',
  'zinsen_kosten_forderung',
  'unverz_kosten_forderung',
  'verz_kosten_forderung',
  'gesamt_forderung',
];
const ZAHLUNG_AUFSTELLUNG_KEYS = [
  'hauptforderung_zahlung',
  'zinsen_hf_zahlung',
  'zinsen_kosten_zahlung',
  'unverz_kosten_zahlung',
  'verz_kosten_zahlung',
  'gesamt_zahlung',
];
const SALDO_KEYS = [
  'hauptforderung_saldo',
  'zinsen_hf_saldo',
  'zinsen_kosten_saldo',
  'unverz_kosten_saldo',
  'verz_kosten_saldo',
  'gesamt_saldo',
];
const DAILY_INTEREST_KEYS = ['taegl_zinsen_hf', 'taegl_zinsen_kosten'];
const PERMISSION_KEYS = ['rzv_erlaubt', 'std_erlaubt', 'vgl_erlaubt', 'rzv_sperre', 'std_sperre', 'vgl_sperre'];
const GLSETTING_KEYS = [
  'glsetting_rzv_erlaubt_g',
  'glsetting_rzv_erlaubt_v',
  'glsetting_rzv_erlaubt_ue',
  'glsetting_std_erlaubt_g',
  'glsetting_std_erlaubt_v',
  'glsetting_std_erlaubt_ue',
  'glsetting_vgl_erlaubt_g',
  'glsetting_vgl_erlaubt_v',
  'glsetting_vgl_erlaubt_ue',
];
const SENSITIVE_DETAIL_KEYS = new Set(['pin']);
const AKTE_KNOWN_KEYS = [
  ...AKTE_MASTER_KEYS,
  ...FORDERUNG_KEYS,
  ...ZAHLUNG_AUFSTELLUNG_KEYS,
  ...SALDO_KEYS,
  ...DAILY_INTEREST_KEYS,
  ...PERMISSION_KEYS,
  ...GLSETTING_KEYS,
];
const DETAIL_LABEL_KEYS = new Set([
  ...AKTE_KNOWN_KEYS,
  'aktenzeichen',
  'rechnungsbetrag',
  'rechnungsdatum',
  'rechnungsnummer',
  'forderungsgrund',
  'zahlungsbetrag',
  'zahlungsdatum',
  'event_code',
  'anzahl_raten',
  'zahlungsart',
  'aktuelle_rate_datum',
  'aktuelle_rate_hoehe',
  'naechste_rate_datum',
  'naechste_rate_hoehe',
  'letzte_rate_datum',
  'letzte_rate_hoehe',
  'titelbetrag',
  'titeldatum',
  'titelnummer',
  'titelbezeichnung',
  'titelgericht',
  'clz',
  'name',
  'strasse',
  'plz',
  'ort',
  'telefon',
  'email',
  'webseite',
  'bank_name',
  'bank_iban',
  'bank_bic',
  'oeffnungszeiten',
]);

interface DetailEntry {
  key: string;
  label: string;
  value: string;
  rawValue: unknown;
}

interface TestAkte {
  label: string;
  aktennummer: string;
  unterakte?: string;
  has_active_rzv?: boolean | number | string;
  has_active_title?: boolean | number | string;
  zv_aktiv?: boolean | number | string;
  strittig?: boolean;
}

interface LoginResponse {
  success: boolean;
  data?: {
    session_id: string;
    session_ttl_seconds?: number;
    budget_contract?: unknown;
    aktennummer: string;
    unterakte: string;
    standort_clz?: unknown;
    standortName?: unknown;
    standort_name?: unknown;
    standort?: unknown;
  };
  error?: string | {
    code?: string;
    message?: string;
  };
  code?: string;
  message?: string;
  requestId?: string;
}

interface LoginErrorEnvelope {
  success?: boolean;
  error?: string | {
    code?: string;
    message?: string;
  };
  code?: string;
  message?: string;
  requestId?: string;
  meta?: {
    requestId?: string;
  };
}

interface TestAktenResponse {
  success: boolean;
  data?: {
    testakten: TestAkte[];
  };
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [DashboardComponent, LanguageSelectorComponent, ImpressumModalComponent, DatenschutzModalComponent, TranslatePipe],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);
  private readonly knowledgeService = inject(KnowledgeService);
  readonly i18n = inject(I18nService);

  // Internal case view is a tester tool: never render it in production builds.
  readonly isDevMode = isDevMode;
  readonly isLoggedIn = signal(false);
  readonly isLoading = signal(false);
  readonly selectedTestAkte = signal('');
  readonly selectedUnterakte = signal('');
  private readonly urlLoginReference = signal(this.readUrlLoginReference());
  readonly loginAktennummer = signal(this.urlLoginReference());
  readonly loginUnterakte = signal('0');
  readonly loginPin = signal('');
  readonly loginError = signal<string | null>(null);
  readonly activeAktennummer = signal('');
  readonly activeStandortClz = signal('');
  readonly activeStandortName = signal('');
  readonly logoStandortClz = computed(() => {
    const backendStandortClz = this.activeStandortClz();
    if (backendStandortClz) {
      return backendStandortClz;
    }

    if (!this.isLoggedIn()) {
      return this.deriveStandortClzFromAktennummer(this.urlLoginReference().split('-')[0].trim());
    }

    return this.deriveStandortClzFromAktennummer(this.activeAktennummer());
  });
  readonly hasLocationHeaderLogo = computed(() => (
    Object.prototype.hasOwnProperty.call(STANDORT_LOGO_SOURCES, this.logoStandortClz())
  ));
  readonly headerLogoSrc = computed(() => STANDORT_LOGO_SOURCES[this.logoStandortClz()] ?? DEFAULT_LOGO_SRC);
  readonly impressumStandortClz = computed(() => (
    Object.prototype.hasOwnProperty.call(STANDORT_LOGO_SOURCES, this.logoStandortClz())
      ? this.logoStandortClz()
      : this.isLoggedIn() ? '217' : ''
  ));
  readonly currentCompanyName = computed(() => (
    this.isLoggedIn()
      ? this.activeStandortName() || 'Creditreform'
      : 'Creditreform'
  ));
  readonly footerCopyright = computed(() => `© 2026 ${this.currentCompanyName()}`);

  readonly testAkten = signal<TestAkte[]>([]);
  readonly showTestAkten = signal(false);

  readonly caseModalOpen = signal(false);
  readonly caseModalLoading = signal(false);
  readonly caseModalError = signal<string | null>(null);
  readonly caseModalData = signal<KnowledgeData | null>(null);
  readonly impressumOpen = signal(false);
  readonly datenschutzOpen = signal(false);

  readonly tracking = inject(TrackingService);

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.inactivityTimer));
    this.tracking.init();
    effect(() => this.tracking.setPage(this.isLoggedIn() ? 'portal' : 'login'));
    effect(() => this.i18n.setCompanyName(this.currentCompanyName()));
    this.clearLegacyAuthStorage();
    try {
      const testakten = new URLSearchParams(window.location.search).get('testakten');
      if (testakten === '1') {
        localStorage.setItem('schupo.testakten', '1');
      } else if (testakten === '0') {
        localStorage.removeItem('schupo.testakten');
      }
      this.showTestAkten.set(isDevMode() && localStorage.getItem('schupo.testakten') === '1');
    } catch {
      this.showTestAkten.set(false);
    }
    if (this.showTestAkten()) {
      this.loadTestAkten();
    }

    const storedSession = sessionStorage.getItem(SESSION_KEY);
    if (!storedSession) {
      this.clearChatSessionState();
      return;
    }

    let sessionData: {
      sessionId: string;
      ts: number;
      ttlSeconds?: number;
      aktennummer?: string;
      unterakte?: string;
      standortClz?: unknown;
      standort_clz?: unknown;
      standortName?: unknown;
      standort_name?: unknown;
      standort?: unknown;
    };
    try {
      sessionData = JSON.parse(storedSession) as { sessionId: string; ts: number };
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      this.clearChatSessionState();
      return;
    }

    const storedTtl = sessionData.ttlSeconds;
    this.inactivityTtlMs = typeof storedTtl === 'number' && Number.isInteger(storedTtl) && storedTtl >= 60
      ? storedTtl * 1000 : 0;
    if (Date.now() - sessionData.ts < (this.inactivityTtlMs || SESSION_TTL_MS)) {
      this.chatService.setSessionId(sessionData.sessionId);
      this.activeAktennummer.set(typeof sessionData.aktennummer === 'string' ? sessionData.aktennummer : '');
      this.activeStandortClz.set(this.extractStandortClz(sessionData));
      this.activeStandortName.set(this.extractStandortName(sessionData));
      this.isLoggedIn.set(true);
      this.armInactivityTimer(sessionData.ts);
      this.onPageShow();
      return;
    }

    sessionStorage.removeItem(SESSION_KEY);
    this.clearChatSessionState();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.datenschutzOpen()) {
      return;
    }

    if (this.impressumOpen()) {
      this.closeImpressum();
      return;
    }

    if (this.caseModalOpen()) {
      this.closeCaseModal();
    }
  }

  private loadTestAkten(): void {
    this.http.get<TestAktenResponse>('/api/auth/testakten').subscribe({
      next: (res) => {
        if (res.success && res.data?.testakten) {
          this.testAkten.set(res.data.testakten);
        } else {
          this.testAkten.set([]);
        }
      },
      error: () => {
        this.testAkten.set([]);
      },
    });
  }

  onTestAkteSelect(value: string): void {
    this.selectedTestAkte.set(value);
    if (value) {
      const [aktennummer, unterakte = '0'] = value.split('|');
      this.loginAktennummer.set(`${aktennummer}-${unterakte || '0'}`);
      this.selectedUnterakte.set(unterakte);
      this.loginUnterakte.set(unterakte);
    } else {
      this.selectedUnterakte.set('');
      this.loginUnterakte.set('0');
    }
    this.loginError.set(null);
  }

  testAkteStatus(akte: TestAkte): string {
    const statuses: string[] = [];
    if (this.isActiveFlag(akte.has_active_title)) {
      statuses.push(this.i18n.t('login.titleActive'));
    }
    if (this.isActiveFlag(akte.zv_aktiv)) {
      statuses.push(this.i18n.t('login.zvActive'));
    }
    if (this.isActiveFlag(akte.has_active_rzv)) {
      statuses.push(this.i18n.t('login.rzvActive'));
    }
    if (akte.strittig === true) {
      statuses.push(this.i18n.t('login.disputed'));
    }
    return statuses.join(', ');
  }

  onAktennummerInput(value: string): void {
    this.loginAktennummer.set(value);
    this.loginUnterakte.set('0');
    this.selectedTestAkte.set('');
    this.selectedUnterakte.set('');
    this.loginError.set(null);
  }

  onPinInput(value: string): void {
    this.loginPin.set(value);
    this.loginError.set(null);
  }

  private isActiveFlag(value: boolean | number | string | undefined): boolean {
    return value === true || value === 1 || value === '1';
  }

  onLoginKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.login();
    }
  }

  openCaseModal(): void {
    this.caseModalOpen.set(true);
    this.caseModalLoading.set(true);
    this.caseModalError.set(null);

    this.knowledgeService.getKnowledge().subscribe({
      next: (data) => {
        this.caseModalData.set(data);
        this.caseModalLoading.set(false);
      },
      error: () => {
        this.caseModalData.set(null);
        this.caseModalError.set(this.i18n.t('caseView.loadError'));
        this.caseModalLoading.set(false);
      },
    });
  }

  closeCaseModal(): void {
    this.caseModalOpen.set(false);
  }

  openImpressum(): void {
    this.impressumOpen.set(true);
  }

  closeImpressum(): void {
    this.impressumOpen.set(false);
  }

  login(): void {
    this.tracking.submitLogin();
    const { aktennummer, unterakte } = this.normalizedLoginReference();
    const pin = this.loginPin().trim();

    if (!aktennummer || !pin) {
      this.tracking.error('validation');
      this.loginError.set(this.i18n.t('login.errors.MISSING_FIELDS'));
      return;
    }

    this.isLoading.set(true);
    this.loginError.set(null);

    const body = { aktennummer, unterakte, pin, language: this.i18n.language() };

    this.http.post<LoginResponse>('/api/auth/login', body).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.tracking.loginSuccess();
          const sessionId = res.data.session_id;
          this.lifecycleTestMode = res.data.budget_contract !== undefined;
          const ttl = res.data.session_ttl_seconds;
          this.inactivityTtlMs = typeof ttl === 'number' && Number.isInteger(ttl) && ttl >= 60 ? ttl * 1000 : 0;
          this.clearChatSessionState();
          this.chatService.setSessionId(sessionId);
          this.activeAktennummer.set(res.data.aktennummer);
          this.activeStandortClz.set(this.extractStandortClz(res.data));
          this.activeStandortName.set(this.extractStandortName(res.data));
          sessionStorage.setItem(SESSION_KEY, JSON.stringify({
            sessionId,
            ts: Date.now(),
            ttlSeconds: res.data.session_ttl_seconds,
            aktennummer: res.data.aktennummer,
            unterakte: res.data.unterakte,
            standortClz: this.activeStandortClz(),
            standortName: this.activeStandortName(),
          }));
          this.clearLegacyAuthStorage();
          this.caseModalData.set(null);
          this.caseModalError.set(null);
          this.loginPin.set('');
          this.isLoggedIn.set(true);
          this.armInactivityTimer(Date.now());
          return;
        }

        this.tracking.error('validation');
        this.loginError.set(this.mapLoginErrorCode(this.extractLoginErrorCode(res), res));
      },
      error: (err: HttpErrorResponse) => {
        this.tracking.error(err.status === 0 ? 'network' : 'validation');
        this.isLoading.set(false);
        this.loginError.set(this.mapLoginErrorCode(this.extractLoginErrorCode(err.error), err.error));
      },
    });
  }

  private readUrlLoginReference(): string {
    const reference = new URLSearchParams(window.location.search).get('aktenNummer')?.trim() ?? '';
    return /^\d{6,20}(?:\s*-\s*\d+)?$/.test(reference) ? reference : '';
  }

  private normalizedLoginReference(): { aktennummer: string; unterakte: string } {
    const reference = this.loginAktennummer().trim();
    const combinedMatch = reference.match(/^\s*(\d{6,20})\s*-\s*(\d+)\s*$/);

    if (combinedMatch) {
      return { aktennummer: combinedMatch[1], unterakte: combinedMatch[2] };
    }

    return { aktennummer: reference, unterakte: '0' };
  }

  private extractLoginErrorCode(envelope: unknown): string {
    if (!envelope || typeof envelope !== 'object') {
      return 'UNKNOWN';
    }

    const body = envelope as LoginErrorEnvelope;
    if (typeof body.code === 'string' && body.code) {
      return body.code;
    }

    if (body.error && typeof body.error === 'object' && typeof body.error.code === 'string' && body.error.code) {
      return body.error.code;
    }

    return 'UNKNOWN';
  }

  private mapLoginErrorCode(code: string, envelope?: unknown): string {
    if (code === 'CASE_ACCESS_BLOCKED') {
      const value = envelope && typeof envelope === 'object'
        ? (envelope as { standort?: unknown }).standort : null;
      const standort = typeof value === 'string' ? value.trim() : '';
      return standort
        ? this.i18n.t('login.errors.CASE_ACCESS_BLOCKED_LOCATION', { standort })
        : this.i18n.t('login.errors.CASE_ACCESS_BLOCKED');
    }

    const messages: Record<string, string> = {
      MISSING_FIELDS: this.i18n.t('login.errors.MISSING_FIELDS'),
      INVALID_AKTENNUMMER: this.i18n.t('login.errors.INVALID_AKTENNUMMER'),
      INVALID_UNTERAKTE: this.i18n.t('login.errors.INVALID_UNTERAKTE'),
      INVALID_PIN: this.i18n.t('login.errors.INVALID_PIN'),
      AUTH_FAILED: this.i18n.t('login.errors.AUTH_FAILED'),
      AUTH_LOCKED: this.i18n.t('login.errors.AUTH_LOCKED'),
      RATE_LIMITED: this.i18n.t('login.errors.RATE_LIMITED'),
      INTERNAL_ERROR: this.i18n.t('login.errors.INTERNAL_ERROR'),
      SERVICE_UNAVAILABLE: this.i18n.t('login.errors.SERVICE_UNAVAILABLE'),
      MALFORMED_JSON: this.i18n.t('login.errors.MALFORMED_JSON'),
      INVALID_REQUEST_SHAPE: this.i18n.t('login.errors.INVALID_REQUEST_SHAPE'),
      UNSUPPORTED_MEDIA_TYPE: this.i18n.t('login.errors.UNSUPPORTED_MEDIA_TYPE'),
    };

    return messages[code] ?? this.i18n.t('login.errors.UNKNOWN');
  }

  requireReLogin(message?: string): void {
    this.clearActiveSession();
    this.loginPin.set('');
    this.selectedTestAkte.set('');
    this.loginError.set(message?.trim() || this.i18n.t('login.errors.RELOGIN'));
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    this.lifecycleMarker('handler', this.currentSessionIdForLogout() !== null);
    this.sendSessionSignal('/api/auth/session/end');
  }

  @HostListener('window:pageshow')
  onPageShow(): void {
    this.sendSessionSignal('/api/auth/session/resume');
  }

  private lastSessionSignalSeq = 0;
  private inactivityTimer: ReturnType<typeof setTimeout> | undefined;
  private inactivityTtlMs = 0;
  private lifecycleTestMode = false;
  private lifecycleCorrelation = '';

  private lifecycleMarker(stage: string, sidPresent: boolean): void {
    if (this.lifecycleTestMode) {
      console.info('[SESSION-LIFECYCLE]', { stage, sid_present: sidPresent, correlation: this.lifecycleCorrelation });
    }
  }

  @HostListener('document:pointerdown')
  @HostListener('document:keydown')
  onSessionActivity(): void {
    if (!this.isLoggedIn() || this.inactivityTtlMs === 0) return;
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (Date.now() - data.ts >= this.inactivityTtlMs) {
        this.logout('TIMEOUT');
        return;
      }
      data.ts = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    }
    this.armInactivityTimer(Date.now());
    this.onPageShow();
  }

  private armInactivityTimer(activityAt: number): void {
    clearTimeout(this.inactivityTimer);
    if (this.inactivityTtlMs > 0) {
      this.inactivityTimer = setTimeout(() => this.logout('TIMEOUT'),
        Math.max(0, activityAt + this.inactivityTtlMs - Date.now()));
    }
  }

  private sendSessionSignal(url: string): void {
    const sessionId = this.currentSessionIdForLogout();
    if (this.lifecycleTestMode && url.endsWith('/end')) {
      this.lifecycleCorrelation = crypto.randomUUID().replaceAll('-', '').slice(0, 16);
    }
    this.lifecycleMarker('sid', sessionId !== null);
    if (sessionId) {
      this.lastSessionSignalSeq = Math.max(this.lastSessionSignalSeq + 1, Date.now());
      void fetch(url, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, signal_seq: this.lastSessionSignalSeq,
          ...(this.lifecycleTestMode ? { lifecycle_correlation: this.lifecycleCorrelation } : {}) }),
      }).then((response) => {
        if (response?.status === 401) this.clearActiveSession();
      }).catch(() => undefined);
      this.lifecycleMarker('fetch_called', true);
    }
  }

  logout(reason: 'LOGOUT' | 'TIMEOUT' = 'LOGOUT'): void {
    const sessionId = this.currentSessionIdForLogout();
    if (sessionId) {
      this.http.post('/api/auth/logout', { session_id: sessionId, ...(reason === 'TIMEOUT' ? { timeout: true } : {}) }).subscribe({
        error: () => undefined,
      });
    }

    this.clearActiveSession();
    this.urlLoginReference.set(this.readUrlLoginReference());
    this.loginAktennummer.set(this.urlLoginReference());
    this.loginUnterakte.set('0');
    this.loginPin.set('');
    this.selectedTestAkte.set('');
    this.selectedUnterakte.set('');
    this.loginError.set(null);
  }

  private clearActiveSession(): void {
    clearTimeout(this.inactivityTimer);
    sessionStorage.removeItem(SESSION_KEY);
    this.activeAktennummer.set('');
    this.activeStandortClz.set('');
    this.activeStandortName.set('');
    this.clearLegacyAuthStorage();
    this.clearChatSessionState();
    this.caseModalData.set(null);
    this.caseModalError.set(null);
    this.caseModalOpen.set(false);
    this.isLoggedIn.set(false);
  }

  private currentSessionIdForLogout(): string | null {
    const storedSession = sessionStorage.getItem(SESSION_KEY);
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession) as { sessionId?: unknown };
        if (typeof sessionData.sessionId === 'string' && sessionData.sessionId.trim() !== '') {
          return sessionData.sessionId;
        }
      } catch {
        // Fall through to the chat session id.
      }
    }

    const chatSessionId = sessionStorage.getItem('chat_session_id');
    return chatSessionId && chatSessionId.trim() !== '' ? chatSessionId : null;
  }

  private clearLegacyAuthStorage(): void {
    for (const storage of [sessionStorage, localStorage]) {
      storage.removeItem('cr_auth');
      storage.removeItem('pin');
    }
  }

  private clearChatSessionState(): void {
    sessionStorage.removeItem(CHAT_MESSAGES_KEY);
    this.chatService.resetSession();
  }

  private extractStandortClz(source: { standort_clz?: unknown; standortClz?: unknown; standort?: unknown }): string {
    const directClz = this.normalizeStandortClz(source.standort_clz ?? source.standortClz);
    if (directClz) {
      return directClz;
    }

    if (source.standort && typeof source.standort === 'object' && 'clz' in source.standort) {
      return this.normalizeStandortClz((source.standort as { clz?: unknown }).clz);
    }

    return '';
  }

  private extractStandortName(source: { standortName?: unknown; standort_name?: unknown; standort?: unknown }): string {
    const directName = this.normalizeStandortName(source.standortName ?? source.standort_name);
    if (directName) {
      return directName;
    }

    if (source.standort && typeof source.standort === 'object' && 'name' in source.standort) {
      return this.normalizeStandortName((source.standort as { name?: unknown }).name);
    }

    return '';
  }


  private deriveStandortClzFromAktennummer(aktennummer: string): string {
    return aktennummer.trim().slice(-3);
  }

  private normalizeStandortClz(value: unknown): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value).padStart(3, '0');
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return '';
  }

  private normalizeStandortName(value: unknown): string {
    const name = typeof value === 'string' ? value.trim() : '';
    return name && !/creditreform/i.test(name) ? `Creditreform ${name}` : name;
  }

  formatCurrency(value: number | string | undefined): string {
    if (value == null) {
      return this.i18n.t('common.notAvailable');
    }

    const numberValue = typeof value === 'string' ? Number.parseFloat(value) : value;
    if (Number.isNaN(numberValue)) {
      return this.i18n.t('common.notAvailable');
    }

    return numberValue.toLocaleString(this.i18n.locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  formatDate(value: string | undefined): string {
    if (!value) {
      return this.i18n.t('common.notAvailable');
    }

    return this.i18n.formatDate(value);
  }

  formatStadium(value: string | undefined): string {
    const map: Record<string, string> = {
      vorgerichtlich: this.i18n.t('format.stage.vorgerichtlich'),
      nachgerichtlich: this.i18n.t('format.stage.nachgerichtlich'),
      gerichtlich: this.i18n.t('format.stage.gerichtlich'),
      ueberwachung: this.i18n.t('format.stage.ueberwachung'),
    };

    return map[value ?? ''] ?? (value ?? this.i18n.t('common.notAvailable'));
  }

  formatYesNo(value: boolean | undefined): string {
    if (value == null) {
      return '–';
    }

    return value ? this.i18n.t('common.yes') : this.i18n.t('common.no');
  }

  formatPaymentMethod(value: string | undefined): string {
    const normalized = (value ?? '').trim().toLowerCase();
    if (normalized === '' || normalized === 'ratenzahlung') {
      return this.i18n.t('caseView.installmentFallback');
    }

    if (normalized === 'lastschrift' || normalized === 'sepa') {
      return this.i18n.t('quickActionWizard.directDebit');
    }

    if (normalized === 'ueberweisung' || normalized === 'überweisung' || normalized === 'transfer') {
      return this.i18n.t('quickActionWizard.transfer');
    }

    return value ?? this.i18n.t('common.notAvailable');
  }

  masterEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, AKTE_MASTER_KEYS, false);
  }

  forderungEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, FORDERUNG_KEYS, true);
  }

  zahlungAufstellungEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, ZAHLUNG_AUFSTELLUNG_KEYS, true);
  }

  saldoEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, SALDO_KEYS, true);
  }

  dailyInterestEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, DAILY_INTEREST_KEYS, true);
  }

  permissionEntries(record: Record<string, unknown>, permissions?: Record<string, unknown>): DetailEntry[] {
    return [
      ...this.detailEntries(record, PERMISSION_KEYS, true),
      ...this.detailEntries(permissions ?? {}, ['rzv_erlaubt', 'std_erlaubt', 'vgl_erlaubt'], false)
        .map((entry) => ({ ...entry, key: `permissions.${entry.key}`, label: `${entry.label} (${this.i18n.t('caseView.apiPermissions')})` })),
    ];
  }

  glSettingEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, GLSETTING_KEYS, true);
  }

  rowEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.detailEntries(record, Object.keys(record), false);
  }

  unknownAkteEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.unknownEntries(record, AKTE_KNOWN_KEYS);
  }

  unknownRootEntries(record: Record<string, unknown>): DetailEntry[] {
    return this.unknownEntries(record, ROOT_KNOWN_KEYS);
  }

  formatRawCaseJson(value: Record<string, unknown>): string {
    return JSON.stringify(value, null, 2) ?? '';
  }

  rowTrack(record: Record<string, unknown>, index: number): string {
    return String(record['id'] ?? record['rechnungsnummer'] ?? record['zahlungsdatum'] ?? record['titelnummer'] ?? index);
  }

  private unknownEntries(record: Record<string, unknown>, knownKeys: string[]): DetailEntry[] {
    const known = new Set([...knownKeys, ...SENSITIVE_DETAIL_KEYS]);
    return this.detailEntries(
      record,
      Object.keys(record).filter((key) => !known.has(key)),
      false
    );
  }

  private detailEntries(record: Record<string, unknown>, keys: string[], includeMissing: boolean): DetailEntry[] {
    return keys
      .filter((key) => !SENSITIVE_DETAIL_KEYS.has(key.toLowerCase()))
      .filter((key) => includeMissing || Object.prototype.hasOwnProperty.call(record, key))
      .map((key) => ({
        key,
        label: this.detailLabel(key),
        value: this.formatDetailValue(key, record[key]),
        rawValue: record[key],
      }));
  }

  private detailLabel(key: string): string {
    if (!DETAIL_LABEL_KEYS.has(key)) {
      return this.humanizeKey(key);
    }

    const translationKey = `caseView.detailLabels.${key}`;
    const label = this.i18n.t(translationKey);
    return label === translationKey ? this.humanizeKey(key) : label;
  }

  private formatDetailValue(key: string, value: unknown): string {
    if (value == null || value === '') {
      return this.i18n.t('common.notAvailable');
    }

    if (typeof value === 'boolean') {
      return this.formatYesNo(value);
    }

    if (typeof value === 'number') {
      if (this.isBooleanKey(key)) {
        return this.formatYesNo(value !== 0);
      }
      if (this.isCurrencyKey(key)) {
        return this.formatCurrency(value);
      }
      return value.toLocaleString(this.i18n.locale());
    }

    if (typeof value === 'string') {
      if (this.isBooleanKey(key) && this.isBooleanLike(value)) {
        return this.formatYesNo(value.toLowerCase() === 'true' || value === '1');
      }
      if (this.isDateKey(key)) {
        return this.formatDate(value);
      }
      return value;
    }

    return JSON.stringify(value, (_key, nestedValue) => typeof nestedValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(nestedValue)
      ? this.i18n.formatDate(nestedValue)
      : nestedValue, 2) ?? String(value);
  }

  private isCurrencyKey(key: string): boolean {
    return /(betrag|forderung|saldo|zahlung|kosten|zinsen|rate_hoehe)/i.test(key);
  }

  private isDateKey(key: string): boolean {
    return /(datum|date|_at)$/i.test(key);
  }

  private isBooleanKey(key: string): boolean {
    return /(erlaubt|sperre|aktiv|privatperson|glsetting)/i.test(key);
  }

  private isBooleanLike(value: string): boolean {
    return ['true', 'false', '1', '0'].includes(value.toLowerCase());
  }

  private humanizeKey(key: string): string {
    return key
      .replace(/^glsetting_/, 'GlSetting ')
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }
}
